/*
 * The seconds a screen is waiting for: the sixty-second cooldown of "Code erneut senden" and the
 * `Retry-After` of a throttled login (security.md §8). Both are shown as a number that runs down —
 * never as a lockout — so a person knows the door opens again by itself.
 */

import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Returns the seconds left and a function that starts a new countdown. The interval exists only
 * while something is running, and a second `start` replaces the first rather than racing it.
 */
export function useCountdown(): [seconds: number, start: (seconds: number) => void] {
	const [left, setLeft] = useState(0)
	const timer = useRef<ReturnType<typeof setInterval> | null>(null)

	const stop = useCallback(() => {
		if (timer.current !== null) {
			clearInterval(timer.current)
			timer.current = null
		}
	}, [])

	const start = useCallback(
		(seconds: number) => {
			stop()
			const whole = Math.max(0, Math.ceil(seconds))
			setLeft(whole)
			if (whole === 0) return
			timer.current = setInterval(() => {
				setLeft((current) => {
					if (current <= 1) stop()
					return current - 1 <= 0 ? 0 : current - 1
				})
			}, 1000)
		},
		[stop],
	)

	useEffect(() => stop, [stop])

	return [left, start]
}
